import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { Button } from './ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'
import { ChefHat, Globe2, Rocket, SearchCheck, UserRound } from 'lucide-react'
import AuthModal from './auth/AuthModal'
import FoodShowcase from './FoodShowcase'
import { WelcomeNav } from './Nav'

/**
 * Landing Page Component
 * Modern welcome page with brand messaging and authentication gateway
 * Features the tagline "Discover delicious recipes from around the world."
 */
const LandingPage = () => {
  const { user } = useAuth()
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [authMode, setAuthMode] = useState('login') // 'login' or 'signup'
  const [authMessage, setAuthMessage] = useState('')

  useEffect(() => {
    if (typeof window === 'undefined') return

    const params = new URLSearchParams(window.location.search)
    if (params.get('auth') !== 'confirmed') return

    setAuthMode('login')
    setAuthMessage('Email confirmed. You can now sign in.')
    setShowAuthModal(true)

    params.delete('auth')
    const cleanedSearch = params.toString()
    const nextUrl = `${window.location.pathname}${cleanedSearch ? `?${cleanedSearch}` : ''}${window.location.hash}`
    window.history.replaceState({}, '', nextUrl)
  }, [])

  // If user is already authenticated, don't show landing page
  if (user) {
    return null
  }

  const openAuthModal = (mode) => {
    setAuthMode(mode)
    setAuthMessage('')
    setShowAuthModal(true)
  }

  return (
    <>
      {/* LANDING PAGE CONTAINER */}
      <div className="recipe-app-shell min-h-screen bg-recipe-gradient flex flex-col">
        <WelcomeNav onGetStarted={() => openAuthModal('login')} />

        {/* MAIN CONTENT SECTION */}
        <main className="flex flex-1 items-center justify-center px-3 py-10 sm:px-4 sm:py-16 lg:py-24">
          <div className="mx-auto max-w-6xl text-center">

            {/* HERO SECTION */}
            <div className="mb-10 sm:mb-16 lg:mb-20">
              {/* BRAND ACCENT */}
              <div className="inline-flex max-w-[min(100%,20.5rem)] sm:max-w-none items-center gap-1.5 sm:gap-3 bg-glass-warm backdrop-blur-md rounded-full px-3 py-1.5 sm:px-6 sm:py-3 mb-6 sm:mb-8 border border-white/20 animate-fade-in">
                <svg
                  className="h-3.5 w-3.5 sm:h-7 sm:w-7 flex-none text-orange-700"
                  viewBox="0 0 32 32"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <circle cx="15" cy="16" r="9" stroke="currentColor" strokeWidth="2" />
                  <path d="M6 16h18M15 7c2.5 2.4 3.8 5.4 3.8 9S17.5 22.6 15 25M15 7c-2.5 2.4-3.8 5.4-3.8 9S12.5 22.6 15 25" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                  <path d="M24 8v7M27 8v7M24 11.5h3M25.5 15v8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
                <span className="text-center text-[11px] sm:text-left sm:text-lg font-semibold leading-none sm:leading-snug text-warm-charcoal whitespace-nowrap">
                  Bringing the world's flavors to your kitchen
                </span>
              </div>

              {/* MAIN TAGLINE - Enhanced Typography */}
              <h1 className="text-[3.25rem] xs:text-5xl sm:text-hero lg:text-hero-lg font-bold text-white mb-8 leading-[1.05] tracking-tight animate-slide-up max-w-5xl mx-auto">
                <span className="block sm:inline">Discover</span>{' '}
                <span className="block sm:inline bg-gradient-to-r from-orange-100 to-orange-200 bg-clip-text text-transparent">delicious</span>{' '}
                <span className="block sm:inline bg-gradient-to-r from-orange-100 to-orange-200 bg-clip-text text-transparent">recipes</span>{' '}
                <span className="block sm:inline">from</span>{' '}
                <span className="block sm:inline">around the</span>{' '}
                <span className="block sm:inline">world</span>
              </h1>
              
              {/* MODERN FOOD SHOWCASE */}
              <FoodShowcase onDiscoverMore={() => openAuthModal('login')} />
              
              {/* SUPPORTING TEXT - Enhanced */}
              <p className="mx-auto mb-8 max-w-3xl animate-slide-up px-1 text-sm font-light leading-relaxed text-white/95 animation-delay-200 sm:mb-12 sm:text-body-lg lg:text-subtitle">
                Join thousands of food lovers exploring <strong className="font-semibold text-orange-100">authentic recipes</strong>,
                ingredients, and culinary traditions from every corner of the globe
              </p>
            </div>

            {/* ENHANCED CALL TO ACTION SECTION */}
            <div className="relative mb-10 animate-scale-in overflow-hidden rounded-2xl border border-white/30 bg-glass-warm p-5 shadow-warm backdrop-blur-lg sm:mb-16 sm:rounded-3xl sm:p-10 lg:mb-20 lg:p-12">
              {/* Background Decoration */}
              <div className="absolute top-0 right-0 h-24 w-24 rounded-full bg-orange-400/10 blur-3xl sm:h-32 sm:w-32"></div>
              <div className="absolute bottom-0 left-0 h-16 w-16 rounded-full bg-orange-300/10 blur-2xl sm:h-24 sm:w-24"></div>

              {/* Content */}
              <div className="relative z-10">
                <h3 className="mb-3 animate-slide-up text-2xl font-bold text-white sm:mb-6 sm:text-title lg:text-display">
                  Ready to start <span className="bg-gradient-to-r from-orange-100 to-orange-200 bg-clip-text text-transparent">cooking?</span>
                </h3>

                <p className="mx-auto mb-6 max-w-2xl animate-slide-up text-sm font-light leading-relaxed text-white/95 animation-delay-100 sm:mb-10 sm:text-body-lg lg:text-subtitle">
                  Join our community and unlock access to <strong className="font-semibold text-orange-100">thousands of amazing recipes</strong> from around the world
                </p>

                {/* ENHANCED AUTH BUTTONS */}
                <div className="flex animate-slide-up flex-col items-center justify-center gap-3 animation-delay-200 sm:flex-row sm:gap-6">
                  <Button
                    onClick={() => openAuthModal('signup')}
                    size="lg"
                    className="w-full transform rounded-xl border-0 bg-white px-6 py-3 text-sm font-bold text-orange-600 shadow-modern-lg transition-all duration-300 hover:-translate-y-1 hover:bg-orange-50 hover:text-orange-700 hover:shadow-modern-xl sm:w-auto sm:rounded-2xl sm:px-10 sm:py-4 sm:text-lg"
                  >
                    <span className="flex items-center gap-2">
                      Get Started Free
                      <Rocket className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" strokeWidth={2.4} />
                    </span>
                  </Button>

                  <Button
                    onClick={() => openAuthModal('login')}
                    variant="outline"
                    size="lg"
                    className="w-full transform rounded-xl border-2 border-white/40 bg-white/15 px-6 py-3 text-sm font-bold text-white shadow-glass backdrop-blur-md transition-all duration-300 hover:-translate-y-1 hover:border-white/60 hover:bg-white/25 sm:w-auto sm:rounded-2xl sm:px-10 sm:py-4 sm:text-lg"
                  >
                    <span className="flex items-center gap-2">
                      Sign In
                      <UserRound className="h-4 w-4 text-orange-600 sm:h-5 sm:w-5" aria-hidden="true" strokeWidth={2.4} />
                    </span>
                  </Button>
                </div>

                {/* Trust Indicators */}
                <div className="mt-5 flex animate-fade-in flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs font-semibold text-white/85 animation-delay-300 sm:mt-8 sm:gap-x-8 sm:gap-y-3 sm:text-lg">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <span className="text-base text-green-300 sm:text-xl">✓</span>
                    <span>Free to join</span>
                  </div>
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <span className="text-base text-green-300 sm:text-xl">✓</span>
                    <span>10,000+ recipes</span>
                  </div>
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <span className="text-base text-green-300 sm:text-xl">✓</span>
                    <span>Expert curated</span>
                  </div>
                </div>
              </div>
            </div>

            {/* ENHANCED FEATURES GRID */}
            <div className="grid gap-4 md:grid-cols-3 md:gap-8 lg:gap-10">

              {/* Feature 1 - Global Recipes */}
              <Card className="group animate-slide-up overflow-hidden rounded-xl border border-white/30 bg-glass-light backdrop-blur-lg transition-all duration-500 hover:border-white/50 hover:shadow-warm sm:rounded-2xl">
                <CardHeader className="pb-3 text-center sm:pb-4">
                  <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-orange-500 shadow-modern transition-all duration-300 group-hover:scale-110 group-hover:shadow-glow sm:mb-4 sm:h-16 sm:w-16 sm:rounded-2xl">
                    <Globe2 className="h-6 w-6 text-white sm:h-8 sm:w-8" aria-hidden="true" strokeWidth={2.25} />
                  </div>
                  <CardTitle className="text-base font-bold text-warm-charcoal transition-colors duration-300 group-hover:text-white sm:text-xl">
                    Global Recipes
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <CardDescription className="text-center text-sm leading-relaxed text-white/95 sm:text-base">
                    Explore <strong className="text-black">authentic dishes</strong> from cuisines worldwide with detailed ingredients and step-by-step instructions.
                  </CardDescription>
                </CardContent>

                {/* Hover Effect */}
                <div className="pointer-events-none absolute inset-0 rounded-xl bg-gradient-to-t from-orange-50/50 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100 sm:rounded-2xl"></div>
              </Card>

              {/* Feature 2 - Smart Search */}
              <Card className="group animate-slide-up overflow-hidden rounded-xl border border-white/30 bg-glass-light backdrop-blur-lg transition-all duration-500 animation-delay-100 hover:border-white/50 hover:shadow-warm sm:rounded-2xl">
                <CardHeader className="pb-3 text-center sm:pb-4">
                  <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-orange-500 shadow-modern transition-all duration-300 group-hover:scale-110 group-hover:shadow-glow sm:mb-4 sm:h-16 sm:w-16 sm:rounded-2xl">
                    <SearchCheck className="h-6 w-6 text-white sm:h-8 sm:w-8" aria-hidden="true" strokeWidth={2.25} />
                  </div>
                  <CardTitle className="text-base font-bold text-warm-charcoal transition-colors duration-300 group-hover:text-white sm:text-xl">
                    Smart Search
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <CardDescription className="text-center text-sm leading-relaxed text-white/95 sm:text-base">
                    Find exactly what you're craving with our <strong className="text-black">intelligent recipe search</strong> and advanced filtering system.
                  </CardDescription>
                </CardContent>

                {/* Hover Effect */}
                <div className="pointer-events-none absolute inset-0 rounded-xl bg-gradient-to-t from-orange-50/50 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100 sm:rounded-2xl"></div>
              </Card>

              {/* Feature 3 - Expert Curated */}
              <Card className="group animate-slide-up overflow-hidden rounded-xl border border-white/30 bg-glass-light backdrop-blur-lg transition-all duration-500 animation-delay-200 hover:border-white/50 hover:shadow-warm sm:rounded-2xl">
                <CardHeader className="pb-3 text-center sm:pb-4">
                  <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-orange-500 shadow-modern transition-all duration-300 group-hover:scale-110 group-hover:shadow-glow sm:mb-4 sm:h-16 sm:w-16 sm:rounded-2xl">
                    <ChefHat className="h-6 w-6 text-white sm:h-8 sm:w-8" aria-hidden="true" strokeWidth={2.25} />
                  </div>
                  <CardTitle className="text-base font-bold text-warm-charcoal transition-colors duration-300 group-hover:text-white sm:text-xl">
                    Expert Curated
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <CardDescription className="text-center text-sm leading-relaxed text-white/95 sm:text-base">
                    Every recipe is <strong className="text-black">carefully selected and verified</strong> for authenticity and deliciousness by our culinary experts.
                  </CardDescription>
                </CardContent>

                {/* Hover Effect */}
                <div className="pointer-events-none absolute inset-0 rounded-xl bg-gradient-to-t from-orange-50/50 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100 sm:rounded-2xl"></div>
              </Card>
            </div>
          </div>
        </main>

        {/* FOOTER */}
        <footer className="bg-white px-6 py-6">
          <div className="relative flex flex-col items-center gap-4 text-center text-sm font-semibold md:min-h-8 md:flex-row md:justify-between md:text-base">
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-brand md:justify-start md:whitespace-nowrap">
              <Link to="/terms" className="transition-colors duration-200 hover:text-black">
                Terms and Conditions
              </Link>
              <Link to="/privacy" className="transition-colors duration-200 hover:text-black">
                Privacy Policy
              </Link>
            </div>

            <p className="text-black md:absolute md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:whitespace-nowrap">
              © 2026 <span className="text-brand">It's Yummtime.</span> Bringing the world's flavors to your kitchen. All Rights Reserved
            </p>

            <div className="flex items-center gap-4 md:whitespace-nowrap md:text-right">
              <Link to="/support" className="text-brand transition-colors duration-200 hover:text-black">
                Support
              </Link>
              <p className="text-black">v0.1</p>
            </div>
          </div>
        </footer>
      </div>

      {/* AUTHENTICATION MODAL */}
      {showAuthModal && (
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          initialMode={authMode}
          initialMessage={authMessage}
        />
      )}
    </>
  )
}

export default LandingPage
